<template>
    <div>
        <div>
            <b-row>
                <b-col cols="12">
                    <SearchComponent v-model="storeFilter" :label="`Store`" :search-u-r-l="`/Store`" :prepopulate="true"
                        :clearable="true" @input="storeFilterChanged" />
                </b-col>
            </b-row>
            <b-row v-if="cards">
                <b-col>
                    <checkbox v-model="pharmacistCreatedErrors" @change="loadErrors">Only My Submitted Errors</checkbox>
                </b-col>
            </b-row>
            <b-row v-if="!hideStatusFilter">
                <b-col class="d-flex flex-wrap" style="gap: 15px;">
                    <checkbox v-model="filterCreated">Created</checkbox>
                    <checkbox v-model="filterResolved">Resolved</checkbox>
                    <checkbox v-model="filterAccepted">Accepted</checkbox>
                    <checkbox v-model="filterAwaitingResponse">Awaiting Response</checkbox>
                    <checkbox v-model="pharmacistAssignedErrors" @change="loadErrors">Only Assigned to Me</checkbox>
                </b-col>
            </b-row>
        </div>
        <hr>
        <!-- Table mode for technicians -->
        <el-table ref="errorsList" v-if="!cards" :data="errors" :empty-text="'No ' + dispenseErrorLabel + 's found.'"
            class="table-shopping">
            <!-- TABLE MODE -->
            <el-table-column property="created" label="Date">
                <template v-slot="props">
                    {{ props.row.created | formatDate('L LT') }}
                </template>
            </el-table-column>
            <el-table-column property="createdBy" label="Reported By"></el-table-column>
            <el-table-column property="rxID" label="RxID"></el-table-column>
            <el-table-column property="reason" label="Reason"></el-table-column>
            <el-table-column property="errorTypeDesc" label="Error Type" sortable></el-table-column>
            <el-table-column property="userNameAssignedTo" label="Assigned To" sortable></el-table-column>
            <el-table-column property="productNameShort" label="Drug"></el-table-column>
            <el-table-column property="updated" label="Updated">
                <template v-slot="props">
                    {{ props.row.created | formatDate('L LT') }}
                </template>
            </el-table-column>
            <el-table-column property="status" label="Status">
                <template v-slot="props">
                    <b-badge :variant="statusStyle(props.row.statusEnum)">
                        {{ props.row.status }}
                    </b-badge>
                </template>
            </el-table-column>

            <el-table-column property="errorType" width="50">
                <template slot-scope="props">
                    <router-link
                        v-if="props.row.statusEnum == statusEnums.Created || props.row.statusEnum == statusEnums.AwaitingResponse"
                        :to="{
                            name: 'PrescriptionDetailsId'
                            , params: { rxId: props.row.rxID, dispenseErrorId: props.row.id }
                        }">
                        <i class="now-ui-icons ui-1_zoom-bold" v-b-tooltip.hover title="Check and Resolve"></i>
                    </router-link>
                    <a href="#" @click.prevent="openErrorDispenseModal(props.row.id)"
                        v-if="props.row.statusEnum == statusEnums.Resolved">
                        <i class="now-ui-icons ui-1_check" v-b-tooltip.hover title="Check and Approve"></i>
                    </a>
                </template>
            </el-table-column>
        </el-table>

        <!-- CARDS MODE for pharmacist -->
        <div v-else>
            <b-row v-if="errors.length">
                <b-col cols="3" v-for="(err, index) in errors" :key="err.id">
                    <DispensingErrorCardComponent v-model="errors[index]" @onClickDetails="openErrorDispenseModal" />
                </b-col>
            </b-row>
            <div v-else class="text-center p-4">
                <b>No {{ dispenseErrorLabel + "s" }} found that are pending to approve.</b>
            </div>
        </div>

        <DispensingErrorModalComponent v-model="selectedDispenseError" :review-mode="true" :show-buttons="false"
            @changed-type="loadErrors" />
    </div>
</template>

<script lang="ts">
import Axios from 'axios';
import { Table, TableColumn } from "element-ui";
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { DispenseError } from '@/models/DispenseError';
import { Store } from '@/models/Store';

import DispensingErrorModalComponent from '@/components/DispensingError/DispensingErrorModalComponent.vue';
import DispensingErrorCardComponent from '@/components/DispensingError/DispensingErrorCardComponent.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import DispenseErrorStatusEnum = DispenseError.DispenseErrorStatusEnum;
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "DispenseErrorListComponent",
    components: {
        DispensingErrorModalComponent, DispensingErrorCardComponent,
        SearchComponent, Checkbox,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn
    }
})
export default class DispenseErrorListComponent extends Vue {
    @Prop({ default: false, type: Boolean }) private cards!: boolean;
    @Prop({ default: false, type: Boolean }) private hideStatusFilter!: boolean;
    @Prop({ default: "default", type: String }) private mode!: string;

    private statusEnums = DispenseError.DispenseErrorStatusEnum;
    private errors: Array<DispenseError> = [];
    private selectedDispenseError: DispenseError = new DispenseError();
    private storeFilter: Store = new Store();
    private pharmacistCreatedErrors = false;
    private pharmacistAssignedErrors = false;
    private filterCreated = true;
    private filterAccepted = false;
    private filterResolved = false;
    private filterAwaitingResponse = true;

    created() {
        this.storeFilter.id = this.persistentStoreFilterId || 0;

        this.handleMode();
        this.fetchDispenseErrorLabel();
    }

    @Watch("mode")
    handleMode() {
        this.filterCreated = true;
        this.filterAccepted = false;
        this.filterResolved = false;
        this.filterAwaitingResponse = true;

        if (this.mode === "pending") {
            this.filterResolved = true;
        } else if (this.mode === "assigned") {
            this.pharmacistAssignedErrors = true;
        }

        this.loadErrors();
    }

    @Watch("strStatusFilter")
    async loadErrors() {

        try {
            const response = await Axios.get("DispenseError", {
                params: {
                    statusFilter: this.strStatusFilter,
                    storeID: this.storeFilter?.id
                }
            });

            this.errors = response.data.map((d: DispenseError) => Object.assign(new DispenseError(), d));

            if (this.errors?.length)
                this.errors = this.errors.sort(this.compareFunc);

            if (this.pharmacistCreatedErrors) {
                this.errors = this.errors.filter(e => e.createdBy?.toLowerCase() === e.viewedBy?.toLowerCase())
            }

            if (this.pharmacistAssignedErrors) {
                this.errors = this.errors.filter(e => e.assignedTo?.toLowerCase() == e.viewedBy?.toLowerCase())
            }
        } catch (error) {
            console.error("Error while listing errors.", { error, response: (error as any)?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    private compareFunc = (a: DispenseError, b: DispenseError): number => {
        const compStatus = this.statusPriority(a.statusEnum) - this.statusPriority(b.statusEnum);
        if (compStatus) return compStatus;

        const dateA = new Date(a.created);
        const dateB = new Date(b.created);
        return dateA.getTime() - dateB.getTime();
    }

    statusPriority(statusNbr: DispenseError.DispenseErrorStatusEnum): number {
        if (statusNbr == DispenseError.DispenseErrorStatusEnum.Resolved) return 0;
        if (statusNbr == DispenseError.DispenseErrorStatusEnum.Created) return 1;
        if (statusNbr == DispenseError.DispenseErrorStatusEnum.Accepted) return 2;
        if (statusNbr == DispenseError.DispenseErrorStatusEnum.AwaitingResponse) return 3;
        return 100;
    }

    get persistentStoreFilterId(): number | null {
        const val = Number(localStorage.getItem('dispenseError_storeFilterId'));
        return val;
    }

    set persistentStoreFilterId(storeId: number | null) {
        localStorage.setItem('dispenseError_storeFilterId', (storeId || 0).toString());
    }

    get strStatusFilter(): string {
        let res = '';
        if (this.selectedStatusFilter?.length)
            res = this.selectedStatusFilter.map(f => Number(f).toString()).reduce((f1, f2) => f1 + ',' + f2)
        return res;
    }

    get selectedStatusFilter(): Array<DispenseError.DispenseErrorStatusEnum | null> {
        const res: Array<DispenseError.DispenseErrorStatusEnum | null> = [
            this.filterCreated ? DispenseError.DispenseErrorStatusEnum.Created : null
            , this.filterResolved ? DispenseError.DispenseErrorStatusEnum.Resolved : null
            , this.filterAccepted ? DispenseError.DispenseErrorStatusEnum.Accepted : null
            , this.filterAwaitingResponse ? DispenseError.DispenseErrorStatusEnum.AwaitingResponse : null
        ].filter(st => st != null);

        return res;
    }

    openErrorDispenseModal(dispenseErrorId: number) {
        // Load Error
        Axios.get(`DispenseError/${dispenseErrorId}`)
            .then(response => {
                this.selectedDispenseError = response.data;
                //Open modal with error details
                this.$bvModal.show("dispense-error-modal");
            })
            .catch(err => {
                console.error("Error while getting error details", err);
                this.$notification(NotificationOptions.error(err));
            })

    }

    statusStyle(status: DispenseError.DispenseErrorStatusEnum) {
        switch (status) {
            case DispenseErrorStatusEnum.Accepted: return "info";
            case DispenseErrorStatusEnum.Resolved: return "success";
            case DispenseErrorStatusEnum.AwaitingResponse: return "danger";
            default: return "warning";
        }
    }

    storeFilterChanged(store: Store) {
        this.persistentStoreFilterId = Number(store?.id);
        this.loadErrors();
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }
}

</script>
